"use client";

import { useState, useCallback } from "react";

import type { IGeocodeAddressPublicItem } from "@/providers/store/api/find-my-school/find-my-school.interface";

export interface UserLocation {
  lat: number;
  lng: number;
  formattedAddress?: string;
}

export function useUserLocation() {
  const [location, setLocation] = useState<UserLocation | null>(null);
  const [isLocating, setIsLocating] = useState(false);

  const setFromGeocodeResult = useCallback((result: IGeocodeAddressPublicItem) => {
    setLocation({
      lat: result.latitude,
      lng: result.longitude,
      formattedAddress: result.formattedAddress,
    });
  }, []);

  const setFromCoordinates = useCallback((lat: number, lng: number) => {
    setLocation({
      lat,
      lng,
    });
  }, []);

  const clearLocation = useCallback(() => {
    setLocation(null);
  }, []);

  const getCurrentPosition = useCallback(() => {
    if (!navigator.geolocation) {
      console.warn("Geolocation is not supported by this browser");
      return;
    }

    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
        setIsLocating(false);
      },
      (error) => {
        console.error("Error getting current position:", error);
        setIsLocating(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    );
  }, []);

  return {
    location,
    isLocating,
    setFromGeocodeResult,
    setFromCoordinates,
    clearLocation,
    getCurrentPosition,
  };
}
